import { useMutation } from '@apollo/client/react';
import {
  Popover,
  Portal,
  Button,
  Spinner,
  VStack,
  Textarea,
  Text,
  HStack,
  Box,
} from '@chakra-ui/react';
import React from 'react';
import { FiZap } from 'react-icons/fi';

import { toaster } from '#src/components/ui/toaster';
import { PROCESS_PROMPT } from '#src/graphql/mutations/process-prompt';

interface PromptCommandProps {
  /** Document ID - required for processing prompts */
  documentId?: string;
  /** Whether this is a new document (not yet saved) */
  isNewDocument: boolean;
  /** Callback to update the document value after AI processing */
  onValueUpdate: (value: string) => void;
}

function usePromptSubmit({
  documentId,
  isNewDocument,
  onValueUpdate,
}: PromptCommandProps) {
  const [processPrompt, { loading: processingPrompt }] =
    useMutation(PROCESS_PROMPT);

  const disabled = processingPrompt || isNewDocument;

  const handleSubmit = React.useCallback(
    async (promptText: string) => {
      if (!promptText.trim()) return;
      if (!documentId) {
        toaster.create({
          title: 'Error',
          description: 'Save the document first to use AI modifications.',
          type: 'error',
        });
        return;
      }

      try {
        const { data: promptData } = await processPrompt({
          variables: {
            input: {
              content: [
                {
                  type: 'prompt',
                  name: 'document-edit-ai-prompt',
                  options: {
                    documentId,
                    input: promptText.trim(),
                  },
                },
              ],
            },
          },
        });

        if (
          promptData?.processPrompt?.success &&
          promptData.processPrompt.response
        ) {
          onValueUpdate(promptData.processPrompt.response);
          toaster.create({
            title: 'Success',
            description: 'AI prompt processed successfully',
          });
        } else {
          throw new Error(
            promptData?.processPrompt?.error || 'Failed to process prompt',
          );
        }
      } catch (error) {
        toaster.create({
          title: 'Error',
          description: `Error processing prompt: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
          type: 'error',
        });
      }
    },
    [documentId, onValueUpdate, processPrompt],
  );

  return { handleSubmit, disabled, processingPrompt };
}

/**
 * Inline AI prompt input for the document page footer (textarea + Apply).
 */
export function PromptFooterInput(props: PromptCommandProps) {
  const { isNewDocument } = props;
  const [prompt, setPrompt] = React.useState('');
  const { handleSubmit, disabled, processingPrompt } = usePromptSubmit(props);

  const onSubmit = async () => {
    const text = prompt.trim();
    if (!text) return;
    setPrompt('');
    await handleSubmit(text);
  };

  return (
    <Box width="100%">
      <HStack gap={2} align="flex-end" width="100%">
        <Textarea
          flex={1}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={
            isNewDocument
              ? 'Save the document first to use AI modifications'
              : 'e.g., Summarize this content, Translate to Spanish, Fix grammar errors...'
          }
          rows={2}
          resize="none"
          minH="40px"
          maxH="120px"
          disabled={disabled}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSubmit();
            }
          }}
        />
        <Button
          size="sm"
          colorScheme="purple"
          onClick={onSubmit}
          disabled={!prompt.trim() || disabled}
          loading={processingPrompt}
          loadingText="Applying..."
          flexShrink={0}
        >
          Apply
        </Button>
      </HStack>
    </Box>
  );
}

/**
 * Prompt button (popover) for use where inline footer is not shown.
 */
export function PromptButton({
  documentId,
  isNewDocument,
  onValueUpdate,
}: PromptCommandProps) {
  const [prompt, setPrompt] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const { handleSubmit, disabled, processingPrompt } = usePromptSubmit({
    documentId,
    isNewDocument,
    onValueUpdate,
  });

  const title = isNewDocument
    ? 'Save the document first to use AI modifications'
    : undefined;

  const onPopoverSubmit = async () => {
    if (!prompt.trim()) return;
    const promptText = prompt.trim();
    setPrompt('');
    setIsOpen(false);
    await handleSubmit(promptText);
  };

  const handleCancel = () => {
    setPrompt('');
    setIsOpen(false);
  };

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={(e) => setIsOpen(e.open)}
      positioning={{ placement: 'bottom-start' }}
      size="sm"
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label="AI prompt to edit document"
          title={title ?? 'Use AI to edit this document'}
          disabled={disabled}
          onClick={() => setIsOpen(true)}
        >
          {processingPrompt ? (
            <Spinner size="sm" />
          ) : (
            <FiZap style={{ width: '24px', height: '24px' }} />
          )}
        </button>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            maxW="400px"
            p={4}
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            shadow="lg"
            borderRadius="md"
          >
            <Popover.Arrow>
              <Popover.ArrowTip />
            </Popover.Arrow>
            <VStack gap={3} align="stretch">
              <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                AI Prompt
              </Text>
              <Text fontSize="xs" color="gray.600">
                Provide instructions for how AI should modify this document
              </Text>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g., Summarize this content, Translate to Spanish, Fix grammar errors..."
                rows={4}
                resize="vertical"
                minH="100px"
              />
              <HStack gap={2} justify="flex-end">
                <Button size="sm" variant="ghost" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  colorScheme="purple"
                  onClick={onPopoverSubmit}
                  disabled={!prompt.trim()}
                >
                  Apply
                </Button>
              </HStack>
            </VStack>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}
